import { COVER_PLACEHOLDER_SRC } from '../../shared/config/constants'
import { t } from '../../shared/config/translations'
import type {
	ClientRelease,
	CollectionMasterYearUpdate,
} from '../../shared/types/collection'
import {
	formatDate,
	getDuration,
	getFormattedLastHeardFromHistoryAsString,
} from '../utils/format'
import { openOverlay } from './Overlay'
import { renderRelease } from './release/Release'
import { releaseYearLoadingTemplate } from './release/releaseTemplates'
import { releaseListItemTemplate } from './releaseListTemplates'

export function initReleaseListItem(
	release: ClientRelease,
	index: number,
	dimReleases = true,
): HTMLElement {
	const isHidden = !!release.history?.isHidden
	const fragment = document
		.createRange()
		.createContextualFragment(
			renderReleaseListItemContent(release, index, isHidden).trim(),
		)
	const item = fragment.firstElementChild
	if (!(item instanceof HTMLLIElement)) {
		throw new Error('Failed to render release list item.')
	}

	const dimmed = isHidden && dimReleases
	setReleaseListItemDimState(item, dimmed)
	const openRelease = () => openOverlay(renderRelease(release, dimmed))
	item.addEventListener('click', openRelease)
	item.addEventListener('keydown', (event) => {
		if (event.key !== 'Enter' && event.key !== ' ') return
		event.preventDefault()
		if (!event.repeat) openRelease()
	})
	return item
}

/** Swaps the small list thumbnail for the full cover, e.g. for grid tiles. */
export function useLargeReleaseListCover(item: HTMLElement): void {
	const img = item.querySelector<HTMLImageElement>('img[data-large-src]')
	if (!img?.dataset.largeSrc) return
	img.src = img.dataset.largeSrc
	img.removeAttribute('data-large-src')
}

export function setReleaseListItemDimState(
	item: HTMLLIElement,
	dimmed: boolean,
): void {
	item.querySelector('img')?.classList.toggle('dimmed', dimmed)
}

export function setReleaseListItemMasterYear(
	item: HTMLLIElement,
	update: CollectionMasterYearUpdate,
): void {
	const masterYearEl = item.querySelector<HTMLElement>('[data-master-year]')
	if (!masterYearEl) return
	masterYearEl.innerHTML = getMasterYearHtml({
		master_year: update.masterYear,
		master_year_status: update.status,
	})
}

function renderReleaseListItemContent(
	release: ClientRelease,
	index: number,
	isHidden: boolean,
): string {
	const data = release.data
	const history = release.history
	const year = data.year > 0 ? data.year : null
	const defaultInfo = [year, getReleaseFormatText(data)]
		.filter(Boolean)
		.join(', ')

	return releaseListItemTemplate({
		index,
		releaseId: release.id,
		isFavorite: !!release.isFavorite,
		isHidden,
		coverSrc: data.thumb || data.cover_image || COVER_PLACEHOLDER_SRC,
		largeCoverSrc: data.cover_image || '',
		coverPlaceholderSrc: COVER_PLACEHOLDER_SRC,
		hiddenUntil: getHiddenUntilText(history),
		title: data.title,
		artists: getArtistsText(data),
		defaultInfo,
		masterYear: getMasterYearHtml(data),
		dateAddedLabel: t('collection.dateAddedLabel'),
		dateAdded: formatDate(release.date_added || ''),
		lastHeardLabel: t('release.lastHeardLabel'),
		lastHeard: getLastHeardText(history),
		favoriteLabel: t('release.favoriteLabel'),
	})
}

function getArtistsText(data: ClientRelease['data']): string {
	return (Array.isArray(data.artists) ? data.artists : [])
		.map((artist) => artist.name)
		.filter(Boolean)
		.join(', ')
}

function getReleaseFormatText(data: ClientRelease['data']): string {
	const formats = Array.isArray(data.formats) ? data.formats : []
	const primaryFormat = formats[0]
	if (!primaryFormat) return ''

	return [
		capitalize(primaryFormat.name),
		...(primaryFormat.descriptions || []),
		primaryFormat.text,
	]
		.filter(Boolean)
		.join(', ')
}

function getHiddenUntilText(history: ClientRelease['history']): string {
	if (!history?.hiddenUntil) return ''

	const hiddenUntil = new Date(history.hiddenUntil)
	if (Number.isNaN(hiddenUntil.getTime())) return ''

	const duration = getDuration(hiddenUntil, true)
	if (duration !== 'today') return duration.replace(' ', '')

	const now = new Date()
	const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
	const hiddenUntilDay = Date.UTC(
		hiddenUntil.getFullYear(),
		hiddenUntil.getMonth(),
		hiddenUntil.getDate(),
	)

	return hiddenUntilDay > today ? '1d' : ''
}

function getLastHeardText(history: ClientRelease['history']): string {
	const knownLastHeard = getFormattedLastHeardFromHistoryAsString(
		history?.historyDates ?? [],
	)
	const lastHeardDate =
		knownLastHeard ||
		((history?.unknownDateCount ?? 0) > 0
			? t('release.unknownDateSummary')
			: '')

	return lastHeardDate && typeof history?.listenCount === 'number'
		? `${lastHeardDate}, <span>${t('release.countLabel')}</span> ${history.listenCount}`
		: ''
}

function getMasterYearHtml(
	data: Pick<ClientRelease['data'], 'master_year' | 'master_year_status'>,
): string {
	if (data.master_year && data.master_year > 0)
		return String(data.master_year)
	return data.master_year_status === 'loading'
		? releaseYearLoadingTemplate(t('release.loading'))
		: t('release.noRecord')
}

function capitalize(value?: string): string {
	return value ? value[0].toUpperCase() + value.slice(1) : ''
}
